import type { JobSalary } from "@lossdog/job-watchlists";
import type { ColumnDef, Header, SortingState, Table } from "@tanstack/react-table";
import { cx } from "class-variance-authority";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ArrowDropdownSmallIcon } from "@/assets/icons/general/ArrowDropdownSmall";
// import { ChevronRightIcon } from "@/assets/icons/general/ChevronRight";
import { RoundedUserIcon } from "@/assets/icons/general/RoundedUserIcon";
import { SearchJobs } from "@/components/dashboard/SearchJobsWatchlist";
import JobCost from "@/components/job-salary-ticker/JobCost";
import JobLiquidity from "@/components/job-salary-ticker/JobLiquidity";
import { JobSalaryLastPrice } from "@/components/job-salary-ticker/JobSalaryLastPrice";
import { JobSalaryNetChange } from "@/components/job-salary-ticker/JobSalaryNetChange";
import { JobSalaryYearlyChangesValue } from "@/components/job-salary-ticker/JobSalaryYearlyChange";
import { usePanelContext } from "@/components/shared/layout/PanelContext";
import { Button } from "@/components/ui/buttons/Button/Button";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { Autocomplete } from "@/components/ui/inputs/Autocomplete/Autocomplete";
import type { MenuItem } from "@/components/ui/Menu/Menu";
import { Menu } from "@/components/ui/Menu/Menu";
import CustomTooltipTrigger from "@/components/ui/overlays/Tooltip/CustomTooltipTrigger";
import { Tooltip } from "@/components/ui/overlays/Tooltip/Tooltip";
import type { SortParams } from "@/components/ui/table/Table";
import TableCell from "@/components/ui/table/TableCell";
import { TableTypographyCell } from "@/components/ui/table/TableTypographyCell";
import { VirtualizedTable } from "@/components/ui/table/VirtualizedTable";
import { Typography } from "@/components/ui/text/Typography/Typography";
import { WatchlistDropdown } from "@/components/watchlist/WatchlistDropdown";
import WatchlistTableHeaderCell from "@/components/watchlist/WatchlistTableHeaderCell";
import { useJobSalaries } from "@/hooks/useJobSalaries";
import { useJobTitleUserCounts } from "@/hooks/useJobTitleUserCounts";

const TOP_LOCATIONS = ["chicago", "new york", "san francisco", "los angeles"];

export const sortByToState = (sortBy: SortParams["sortBy"] | undefined) => {
  if (!sortBy) return [];

  const [id, desc] = sortBy.split(".");
  return [{ id, desc: desc === "desc" }];
};

export const stateToSortBy = (sorting: SortingState | undefined) => {
  if (!sorting || sorting.length === 0) return undefined;

  const sort = sorting[0];

  return `${sort.id}.${sort.desc ? "desc" : "asc"}` as const;
};

export const WatchlistPage = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { isLeftPanelMaxed } = usePanelContext();

  const {
    jobSalaries,
    watchlistGroups,
    metroAreas,
    quantiles,
    selectedWatchlistGroup,
    selectedWatchlist,
    selectedMetroArea,
    selectedQuantile,
    setSelectedWatchlistGroup,
    setSelectedMetroArea,
    setSelectedWatchlist,
    setSelectedQuantile,
    isLoading,
  } = useJobSalaries();

  const jobTitles = useMemo(() => {
    return jobSalaries?.map((job) => job.title) || [];
  }, [jobSalaries]);

  const { jobTitleUserCountsMap, isLoading: isUserCountsLoading } = useJobTitleUserCounts({
    jobTitles,
    selectedMetroArea,
    selectedQuantile,
    enabled: !isLoading && jobTitles.length > 0,
  });

  // Wrapper functions to handle type conversion for dropdowns
  const handleQuantileChange = (item: { id: string; title: string } | null) => {
    if (item) {
      // Find the full Quantile object from the quantiles array
      const fullQuantile = quantiles?.find((q) => q.id === item.id);
      setSelectedQuantile(fullQuantile || null);
    } else {
      setSelectedQuantile(null);
    }
  };

  const handleMetroAreaChange = (item: { id: string; title: string } | null) => {
    if (item) {
      // Find the full MetroArea object from the metroAreas array
      const fullMetroArea = metroAreas?.find((m) => m.id === item.id);
      setSelectedMetroArea(fullMetroArea || null);
    } else {
      setSelectedMetroArea(null);
    }
  };

  const [sorting, setSorting] = useState<SortingState>([{ id: "title", desc: false }]);

  // Overlay state (commented out - using dropdowns instead)
  // const [isWatchlistGroupOverlayOpen, setIsWatchlistGroupOverlayOpen] = useState(false);
  // const [isWatchlistOverlayOpen, setIsWatchlistOverlayOpen] = useState(false);

  const columns = useMemo<ColumnDef<JobSalary>[]>(
    () => [
      {
        accessorKey: "title",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.position"), table, "start"),
        size: 200,
        cell: (info) => WatchlistTableCells(info, "title", jobTitleUserCountsMap.get(info.row.original.title)),
        meta: { label: t("watchlist.filters.position") },
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.title.toLowerCase();
          const b = rowB.original.title.toLowerCase();
          return a.localeCompare(b);
        },
      },
      {
        accessorKey: "salary",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.salary"), table, "end"),
        size: 120,
        cell: (info) => WatchlistTableCells(info, "salary"),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.salary.value || 0;
          const b = rowB.original.salary.value || 0;
          return a - b;
        },
        meta: { label: t("watchlist.filters.salary") },
      },
      {
        accessorKey: "change",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.change"), table, "end"),
        size: 120,
        cell: (info) => WatchlistTableCells(info, "change"),
        meta: { label: t("watchlist.filters.change") },
      },
      {
        accessorKey: "netChangeOneYear",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.1ych"), table, "end"),
        size: 70,
        cell: (info) => WatchlistTableCells(info, "netChangeOneYear"),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.netChangeOneYear.value || 0;
          const b = rowB.original.netChangeOneYear.value || 0;
          return a - b;
        },
        meta: { label: t("watchlist.filters.1ych") },
      },
      {
        accessorKey: "netChangeThreeYear",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.3ych"), table, "end"),
        size: 70,
        cell: (info) => WatchlistTableCells(info, "netChangeThreeYear"),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.netChangeThreeYear.value || 0;
          const b = rowB.original.netChangeThreeYear.value || 0;
          return a - b;
        },
        meta: { label: t("watchlist.filters.3ych") },
      },
      {
        accessorKey: "netChangeFiveYear",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.5ych"), table, "end"),
        size: 70,
        cell: (info) => WatchlistTableCells(info, "netChangeFiveYear"),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.netChangeFiveYear.value || 0;
          const b = rowB.original.netChangeFiveYear.value || 0;
          return a - b;
        },
        meta: { label: t("watchlist.filters.5ych") },
      },
      {
        accessorKey: "totalCostProjectionThreeYears",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.3y"), table, "end"),
        size: 120,
        cell: (info) => WatchlistTableCells(info, "totalCostProjectionThreeYears"),
        meta: { label: t("watchlist.filters.3y") },
      },

      {
        accessorKey: "totalCostProjectionFiveYears",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.5y"), table, "end"),
        size: 120,
        cell: (info) => WatchlistTableCells(info, "totalCostProjectionFiveYears"),
        meta: { label: t("watchlist.filters.5y") },
      },
      {
        accessorKey: "averageTenureInYears",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.jobTenure"), table, "end"),
        size: 70,
        cell: (info) => WatchlistTableCells(info, "averageTenureInYears"),
        meta: { label: t("watchlist.filters.jobTenure") },
      },
      {
        accessorKey: "liquidity",
        header: ({ header, table }) => WatchlistHeaderCell(header, t("watchlist.filters.liquidity"), table, "end"),
        size: 70,
        cell: (info) => WatchlistTableCells(info, "liquidity"),
        meta: { label: t("watchlist.filters.liquidity") },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobTitleUserCountsMap, t],
  );

  const sortedTopLocations = useMemo(() => {
    const sortedMetroAreas: { id: string; label: string }[] = [];
    const sortedAlphabetically = metroAreas?.sort((a, b) => a.title.localeCompare(b.title));
    sortedAlphabetically?.forEach((metroArea) => {
      const isTopLocation = TOP_LOCATIONS.some((location) => metroArea.title.toLowerCase().includes(location));
      if (isTopLocation) {
        sortedMetroAreas.unshift({ id: metroArea.title, label: metroArea.title });
      } else {
        sortedMetroAreas.push({ id: metroArea.title, label: metroArea.title });
      }
    });

    return sortedMetroAreas;
  }, [metroAreas]);

  const menuItems: MenuItem[] = useMemo(() => {
    return [
      ...(watchlistGroups?.map((group) => ({
        label: group.title,
        content: group.title,
        onAction:
          group.title === "All"
            ? () => {
                setSelectedWatchlistGroup(group);
                setSelectedWatchlist(null);
              }
            : undefined,
        children:
          group.title !== "All" &&
          group.watchlists.map((watchlist: any) => ({
            label: watchlist.title,
            content: watchlist.title,
            onAction: () => {
              setSelectedWatchlistGroup(group);
              setSelectedWatchlist(watchlist);
            },
          })),
      })) || []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlistGroups]);

  const handleClick = useCallback(() => {
    const allGroup = watchlistGroups?.find((watchlistName) => watchlistName.title === "All");
    if (allGroup) {
      setSelectedWatchlistGroup(allGroup);
      setSelectedWatchlist(null);
    }
  }, [watchlistGroups, setSelectedWatchlistGroup, setSelectedWatchlist]);

  const [search, setSearch] = useState("");

  const filteredJobSalaries = useMemo(() => {
    if (!jobSalaries) return [];
    const searchTermToUse = search.trim().toLowerCase();
    if (!searchTermToUse) return jobSalaries;
    const filtered = jobSalaries.filter((job) => job.title.toLowerCase().includes(searchTermToUse));
    return filtered.slice(0, 50);
  }, [jobSalaries, search]);

  return (
    <div className="flex h-full w-full flex-[1_1_0] flex-col flex-wrap">
      <div className="flex w-full flex-[1_1_0] flex-col items-center px-2-5 py-5">
        <div className="flex w-full flex-[0_0_0] flex-col items-center justify-center gap-3 pb-3">
          <div className="flex w-full max-w-[1300px] flex-row flex-wrap items-center justify-between gap-2">
            <div className={cx("flex w-full flex-col gap-2", isLeftPanelMaxed && "!flex-row")}>
              <div className="flex-grow">
                <SearchJobs
                  search={search}
                  setSearch={setSearch}
                />
              </div>
              <Menu
                items={menuItems}
                trigger={
                  <Button
                    icon={ArrowDropdownSmallIcon}
                    variant="primary"
                    iconPosition="right"
                    className="!border-elevation-outline-1 !bg-elevation-surface-1 !text-text-inverted-tertiary w-full flex-1 border py-0 pr-0 pl-1-5"
                    iconClassName="h-6 w-6 text-text-inverted-tertiary"
                  >
                    <Typography
                      size="body-3"
                      className={cx(
                        "w-full max-w-[100%] truncate text-start text-text-inverted-tertiary",
                        isLeftPanelMaxed && "max-w-[425px]",
                      )}
                    >
                      {selectedWatchlistGroup?.title === "All" ? "All" : ""}
                      {selectedWatchlistGroup?.title !== "All"
                        ? `${selectedWatchlistGroup?.title} > ${selectedWatchlist?.title}`
                        : ""}
                    </Typography>
                  </Button>
                }
                triggerClassName={cx(isLeftPanelMaxed && "w-full")}
              />

              <div className="flex w-full flex-row gap-2">
                {metroAreas && selectedMetroArea && (
                  <Autocomplete
                    items={sortedTopLocations}
                    label=""
                    onSelectionChange={(item) => {
                      const metroArea = metroAreas.find((m) => m.title === item);
                      if (metroArea) {
                        handleMetroAreaChange(metroArea);
                      }
                    }}
                    selectedKey={selectedMetroArea?.title}
                    defaultSelectedKey={selectedMetroArea?.title}
                    inputValue={selectedMetroArea?.title}
                    menuTrigger="focus"
                    variant="filled"
                    placeholder="Select Metro Area"
                    inputClassname={cx(
                      "w-full flex-1 border !border-elevation-outline-1 !bg-elevation-surface-1 !py-0-5 !pl-1-5 !pr-0 !text-text-inverted-tertiary !text-desktop-body-3",
                      isLeftPanelMaxed && "w-full max-w-[350px]",
                    )}
                    inputIconClassname="!h-3-5 !w-3-5 text-text-inverted-tertiary"
                    hideLabels
                    comboboxClassName={cx("w-full", isLeftPanelMaxed && " max-w-[350px]")}
                  />
                )}
                {quantiles && (
                  <WatchlistDropdown
                    data={quantiles}
                    selectedItem={selectedQuantile}
                    setSelectedItem={handleQuantileChange}
                    selectedItemLabel={
                      selectedQuantile && (
                        <Typography
                          size="body-3"
                          className="w-full text-start"
                        >
                          <Typography
                            size="body-3"
                            className="text-text-inverted-secondary"
                            as="span"
                          >
                            Exp:{" "}
                          </Typography>
                          {selectedQuantile?.short}
                        </Typography>
                      )
                    }
                    btnClassName="w-[110px] min-w-[110px] max-w-[110px]"
                    popperClassname="w-[110px]"
                    triggerClassName="!w-[110px]"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <VirtualizedTable
          data={filteredJobSalaries ?? []}
          columns={columns}
          sorting={sorting}
          onSortingChange={setSorting}
          isLoading={isLoading || isUserCountsLoading}
          manualSorting={false}
          className="w-full"
          noBorder
          onRowDataUsed={(rowData) => rowData.subscribe()}
          onRowClick={(rowData) => router.push(`/job/${rowData.id}`)}
          rowHeight={36}
          flexibleColumnId="title" // This makes the title column grow to fill remaining space
          emptyState={
            <div className="">
              {selectedWatchlistGroup?.title === "All" ? (
                <Typography
                  size="body-1"
                  className="mt-6"
                >
                  Sorry Job not found. Search again.
                </Typography>
              ) : (
                <div className="">
                  <Typography
                    size="body-1"
                    className="mt-6"
                  >
                    No results? Try switching search filter to all.
                  </Typography>
                  <Button
                    variant="primary"
                    className="mt-4"
                    onPress={handleClick}
                  >
                    Switch Filter to All
                  </Button>
                </div>
              )}
            </div>
          }
        />
      </div>
    </div>
  );
};

const WatchlistHeaderCell = (
  header: Header<any, unknown>,
  label: string,
  table: Table<JobSalary>,
  align: "start" | "center" | "end",
) => {
  return (
    <WatchlistTableHeaderCell
      header={header}
      label={label}
      align={align}
      table={table}
    />
  );
};

const WatchlistTableCells = (info: any, key: string, profileCount?: number) => {
  const { t } = useTranslation();

  if (key === "title") {
    return (
      <TableCell
        align="start"
        className="h-full w-full py-2"
      >
        <Tooltip
          text={info.row.original.title}
          offset={30}
          placement="top left"
        >
          <CustomTooltipTrigger className="relative w-auto truncate">
            <Typography
              size="body-3"
              className="max-w-[100%] truncate font-body-prominent-2"
            >
              {info.row.original.title}
            </Typography>
          </CustomTooltipTrigger>
        </Tooltip>
        {!!profileCount && (
          <Tooltip
            text={t("watchlist.filters.profilesCount", { count: profileCount, position: info.row.original.title })}
            offset={25}
            placement="top"
          >
            <IconButton
              variant="transparent"
              size="small"
              icon={RoundedUserIcon}
              iconClassName="h-4 w-4"
              className="!p-0 !pl-2 text-text-inverted-secondary"
              label="Profiles"
            />
          </Tooltip>
        )}
      </TableCell>
    );
  }
  if (key === "jobTenure") {
    return (
      <TableCell
        align="end"
        className="py-1"
      >
        <Typography size="body-4">{info.row.original.averageTenureInYears}</Typography>
      </TableCell>
    );
  }
  if (key === "averageTenureInYears") {
    return (
      <TableCell
        align="end"
        className="py-1"
      >
        <Typography
          size="body-3"
          className="font-body-prominent-2 text-text-default-quarternary"
        >
          {info.row.original.averageTenureInYears}
        </Typography>
      </TableCell>
    );
  }
  if (key === "liquidity") {
    return (
      <TableCell
        align="end"
        className="py-1"
      >
        <Typography
          size="body-3"
          className="font-body-prominent-2 text-text-default-quarternary"
        >
          <JobLiquidity liquidity={info.row.original.liquidity} />
        </Typography>
      </TableCell>
    );
  }
  if (key === "change") {
    return (
      <TableCell
        align="end"
        className="py-1"
      >
        <Typography
          size="body-3"
          className="font-body-prominent-2"
        >
          <JobSalaryNetChange jobSalary={info.row.original} />
        </Typography>
      </TableCell>
    );
  }
  if (key === "netChangeOneYear" || key === "netChangeThreeYear" || key === "netChangeFiveYear") {
    return (
      <TableCell
        className="flex flex-row items-center gap-2 py-1"
        align="end"
      >
        <Typography
          size="body-3"
          className="font-body-prominent-2"
        >
          <JobSalaryYearlyChangesValue
            jobSalary={info.row.original}
            propKey={key}
          />
        </Typography>
      </TableCell>
    );
  }
  if (key === "salary") {
    return (
      <TableCell
        align="end"
        className="py-1"
      >
        <Typography
          size="body-3"
          className="font-body-prominent-2"
        >
          <JobSalaryLastPrice jobSalary={info.row.original} />
        </Typography>
      </TableCell>
    );
  }

  if (key === "totalCostProjectionThreeYears") {
    return (
      <TableCell
        align="end"
        className="py-1"
      >
        <Typography
          size="body-3"
          className="font-body-prominent-2"
        >
          <JobCost cost={info.row.original.totalCostProjectionThreeYears} />
        </Typography>
      </TableCell>
    );
  }
  if (key === "totalCostProjectionFiveYears") {
    return (
      <TableCell
        align="end"
        className="py-1"
      >
        <Typography
          size="body-3"
          className="font-body-prominent-2"
        >
          <JobCost cost={info.row.original.totalCostProjectionFiveYears} />
        </Typography>
      </TableCell>
    );
  }
  return (
    <TableTypographyCell
      value={info.getValue()}
      className="!text-desktop-body-3 w-80 py-1 font-body-prominent-2"
    />
  );
};
